import React, { useEffect, useState, useMemo } from "react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { Wrench, Star } from "lucide-react";
import { api } from "@/lib/api";
import { formatCount, formatPct, formatDays } from "@/utils/formatters";
import PageHeader from "@/components/layout/PageHeader";
import GlassCard from "@/components/ui-fleet/GlassCard";
import ClickableKPITile from "@/components/ui-fleet/ClickableKPITile";
import DrillDownPanel from "@/components/ui-fleet/DrillDownPanel";
import StatTableDrillDown from "@/components/ui-fleet/StatTableDrillDown";
import StatusBadge from "@/components/ui-fleet/StatusBadge";

const STATUS_COLORS = {
  "RUNNING WITH NO PROBLEM": "#2D7A4A",
  "RUNNING WITH MINOR PROBLEM": "#B07D1A",
  "RUNNING WITH MAJOR PROBLEM": "#E8884A",
  "BREAKDOWN": "#B03A2A",
};

export default function ServiceHub() {
  const [calls, setCalls] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/service/calls"),
      api.get("/service/engineers"),
      api.get("/service/summary"),
    ]).then(([c, e, s]) => {
      setCalls(c.data);
      setEngineers(e.data);
      setSummary(s.data);
    });
  }, []);

  const groupedCalls = useMemo(() => {
    const groups = { critical: [], warning: [], normal: [], closed: [] };
    for (const c of calls) {
      if (c.call_status === "CLOSED") groups.closed.push(c);
      else if (c.severity === "critical") groups.critical.push(c);
      else if (c.severity === "warning") groups.warning.push(c);
      else groups.normal.push(c);
    }
    return groups;
  }, [calls]);

  if (!summary) return <div><PageHeader title="Service Hub" breadcrumb="Operations" /><div className="text-center py-20" style={{ color: "var(--slate-500)" }}>Loading…</div></div>;

  const k = summary.kpis;
  // Gantt data — pick 8 representative
  const gantt = calls.filter(c => c.fixed_date).sort((a, b) => b.days_to_fix - a.days_to_fix).slice(0, 8);

  return (
    <div data-testid="service-hub">
      <PageHeader title="Service Hub" breadcrumb="Operations" />

      <div className="grid grid-cols-5 gap-4 mb-5">
        <ClickableKPITile label="Open Calls" value={formatCount(k.open_calls)} sub="Tap to see the queue" color="amber" onClick={() => setDrill("open")} testId="kpi-open" />
        <ClickableKPITile label="Critical" value={formatCount(k.critical)} sub="Tap to triage critical only" color="red" onClick={() => setDrill("critical")} testId="kpi-critical" />
        <ClickableKPITile label="Avg Resolution" value={formatDays(k.avg_resolution_days)} sub="Tap for slowest fixes" color="copper" onClick={() => setDrill("avg")} testId="kpi-avg-res" />
        <ClickableKPITile label="SLA Breach" value={formatCount(k.sla_breach_count)} sub="Tap for breached list" color="red" onClick={() => setDrill("sla")} testId="kpi-sla" />
        <ClickableKPITile label="First Fix Rate" value={formatPct(k.first_fix_rate)} sub="Tap for engineer ranking" color="green" onClick={() => setDrill("ffr")} testId="kpi-ffr" />
      </div>

      <div className="grid grid-cols-12 gap-5 mb-5">
        {/* Call queue */}
        <GlassCard className="col-span-5 p-5" data-testid="call-queue">
          <h3 className="font-display text-[16px] mb-3" style={{ color: "var(--slate-900)" }}>Service Call Queue</h3>
          <div className="space-y-4 max-h-[640px] overflow-y-auto">
            {Object.entries(groupedCalls).map(([sev, list]) => list.length > 0 && (
              <div key={sev}>
                <div className="text-[10px] uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: "var(--slate-500)" }}>
                  <span>{sev}</span>
                  <span className="font-mono">{list.length}</span>
                </div>
                <div className="space-y-2">
                  {list.slice(0, 6).map((c) => <CallCard key={c.id} c={c} />)}
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* SLA Gantt */}
        <GlassCard className="col-span-4 p-5" data-testid="sla-gantt">
          <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>SLA Timeline Visualizer</h3>
          <p className="text-[12px] mb-4" style={{ color: "var(--slate-500)" }}>Green = within SLA, red = breached</p>
          <SLAGantt rows={gantt} />
        </GlassCard>

        {/* Engineer Leaderboard */}
        <GlassCard className="col-span-3 p-5" data-testid="engineer-leaderboard">
          <div className="flex items-center gap-2 mb-3">
            <Star size={15} style={{ color: "var(--accent)" }} />
            <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Engineers</h3>
          </div>
          <div className="space-y-3">
            {engineers.slice(0, 6).map((e, i) => (
              <div key={e.name} className="glass-card-sm p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12px] font-medium" style={{ color: "var(--slate-900)" }}>{e.name}</span>
                  <span className="font-mono text-[11px]" style={{ color: "var(--accent)" }}>#{i + 1}</span>
                </div>
                <div className="text-[10px] mb-2 font-mono" style={{ color: "var(--slate-500)" }}>{e.phone}</div>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <Mini label="Calls" value={e.calls} />
                  <Mini label="First Fix" value={`${e.first_fix_rate}%`} />
                  <Mini label="Avg Days" value={e.avg_days} />
                </div>
                <div className="fuel-bar mt-2">
                  <div style={{ width: `${e.rating}%`, background: "var(--accent)" }} />
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Machine status distribution */}
        <GlassCard className="col-span-6 p-5">
          <h3 className="font-display text-[16px] mb-3" style={{ color: "var(--slate-900)" }}>Machine Status Distribution</h3>
          <div className="grid grid-cols-2 gap-4 items-center">
            <div style={{ height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={summary.status_distribution} dataKey="count" nameKey="status"
                       innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {summary.status_distribution.map((d, i) => (
                      <Cell key={i} fill={STATUS_COLORS[d.status] || "#7A726A"} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {summary.status_distribution.map((d) => (
                <div key={d.status} className="flex items-center gap-2 text-[11px]">
                  <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[d.status] || "#7A726A" }} />
                  <span className="flex-1" style={{ color: "var(--slate-700)" }}>{d.status}</span>
                  <span className="font-mono" style={{ color: "var(--slate-900)" }}>{d.count}</span>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>

        <GlassCard className="col-span-6 p-5">
          <h3 className="font-display text-[16px] mb-3" style={{ color: "var(--slate-900)" }}>Failure Description Analysis</h3>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={summary.failure_descriptions.slice(0, 8)} layout="vertical" margin={{ left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,175,164,0.25)" />
                <XAxis type="number" />
                <YAxis dataKey="desc" type="category" width={180} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#C4622D" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      </div>

      <DrillDownPanel
        isOpen={drill !== null}
        onClose={() => setDrill(null)}
        title={
          drill === "open" ? "Open Service Calls"
          : drill === "critical" ? "Critical Open Calls"
          : drill === "avg" ? "Slowest Resolutions"
          : drill === "sla" ? "SLA Breaches"
          : drill === "ffr" ? "Engineer First-Fix Ranking"
          : ""
        }
        subtitle={
          drill === "open" ? `${k.open_calls} calls awaiting closure`
          : drill === "critical" ? `${k.critical} critical · needs triage`
          : drill === "avg" ? `Fleet avg ${formatDays(k.avg_resolution_days)} · top outliers below`
          : drill === "sla" ? `${k.sla_breach_count} breaches · review root causes`
          : drill === "ffr" ? `Fleet FFR ${formatPct(k.first_fix_rate)}`
          : ""
        }
        testId="service-drilldown"
      >
        {drill === "open" && (
          <StatTableDrillDown
            rows={calls.filter((c) => c.call_status !== "CLOSED").slice(0, 30)}
            columns={[
              { key: "call_id", label: "Call", mono: true },
              { key: "machine_id", label: "Machine", mono: true },
              { key: "call_subtype", label: "Type" },
              { key: "service_engineer", label: "Engineer" },
              { key: "severity", label: "Severity" },
            ]}
          />
        )}

        {drill === "critical" && (
          <StatTableDrillDown
            rows={calls.filter((c) => c.severity === "critical" && c.call_status !== "CLOSED")}
            columns={[
              { key: "call_id", label: "Call", mono: true },
              { key: "machine_id", label: "Machine", mono: true },
              { key: "call_subtype", label: "Type" },
              { key: "service_engineer", label: "Engineer" },
              { key: "failure_desc", label: "Failure" },
            ]}
            emptyText="No critical open calls — fleet is steady."
          />
        )}

        {drill === "avg" && (
          <StatTableDrillDown
            rows={[...calls].filter((c) => c.days_to_fix).sort((a, b) => b.days_to_fix - a.days_to_fix).slice(0, 15)}
            columns={[
              { key: "call_id", label: "Call", mono: true },
              { key: "machine_id", label: "Machine", mono: true },
              { key: "days_to_fix", label: "Days", format: (v) => formatDays(v, 3) },
              { key: "service_engineer", label: "Engineer" },
              { key: "call_subtype", label: "Type" },
            ]}
          />
        )}

        {drill === "sla" && (
          <StatTableDrillDown
            rows={calls.filter((c) => c.sla_breached).slice(0, 30)}
            columns={[
              { key: "call_id", label: "Call", mono: true },
              { key: "machine_id", label: "Machine", mono: true },
              { key: "days_to_fix", label: "Days", format: (v) => formatDays(v, 3) },
              { key: "call_subtype", label: "Type" },
              { key: "service_engineer", label: "Engineer" },
            ]}
            emptyText="No SLA breaches — service team on target."
          />
        )}

        {drill === "ffr" && (
          <StatTableDrillDown
            rows={engineers}
            columns={[
              { key: "name", label: "Engineer" },
              { key: "calls", label: "Calls", format: (v) => formatCount(v) },
              { key: "first_fix_rate", label: "FFR", format: (v) => formatPct(v) },
              { key: "avg_days", label: "Avg Days", format: (v) => `${v}d` },
              { key: "rating", label: "Rating", format: (v) => formatPct(v) },
            ]}
          />
        )}
      </DrillDownPanel>
    </div>
  );
}

function CallCard({ c }) {
  const slaStatus = c.sla_breached ? "breached" : c.days_to_fix > 2 ? "at_risk" : "on_time";
  return (
    <div className="glass-card-sm p-3" data-testid={`call-${c.call_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[12px] font-medium" style={{ color: "var(--slate-900)" }}>{c.call_id}</span>
          <span className="status-pill copper" style={{ fontSize: "10px" }}>{c.machine_id}</span>
        </div>
        <StatusBadge status={c.call_type === "Warranty" ? "renewed" : "expired"} label={c.call_type === "Warranty" ? "Warranty" : "BW"} />
      </div>
      <div className="text-[12px] mb-1.5" style={{ color: "var(--slate-700)" }}>{c.call_subtype}</div>
      <div className="text-[11px] mb-2" style={{ color: "var(--slate-500)" }}>
        HMR <span className="font-mono">{c.call_hmr}</span> · {c.service_engineer}
      </div>
      <div className="flex items-center justify-between">
        <Timeline created={c.call_create_date} fixed={c.fixed_date} closed={c.closed_date} />
        <StatusBadge status={slaStatus} />
      </div>
    </div>
  );
}

function Timeline({ created, fixed, closed }) {
  return (
    <div className="flex items-center gap-1 text-[10px]" style={{ color: "var(--slate-500)" }}>
      <Dot active /> <span>Created</span>
      <span style={{ width: 12, height: 1, background: "var(--slate-300)" }}></span>
      <Dot active={!!fixed} /> <span>Fixed</span>
      <span style={{ width: 12, height: 1, background: "var(--slate-300)" }}></span>
      <Dot active={!!closed} /> <span>Closed</span>
    </div>
  );
}
function Dot({ active }) {
  return <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? "var(--accent)" : "var(--slate-300)" }} />;
}
function Mini({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase" style={{ color: "var(--slate-500)" }}>{label}</div>
      <div className="font-mono text-[11px]" style={{ color: "var(--slate-900)" }}>{value}</div>
    </div>
  );
}
function SLAGantt({ rows }) {
  const max = Math.max(30, ...rows.map(r => r.days_to_fix || 0));
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const width = ((r.days_to_fix || 0) / max) * 100;
        const breached = r.sla_breached;
        return (
          <div key={r.id} className="text-[11px]">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono" style={{ color: "var(--slate-900)" }}>{r.call_id}</span>
              <span className="font-mono" style={{ color: breached ? "var(--status-red)" : "var(--status-green)" }}>
                {r.days_to_fix}d
              </span>
            </div>
            <div className="fuel-bar" style={{ height: 8 }}>
              <div style={{
                width: `${width}%`,
                background: breached
                  ? "linear-gradient(90deg, var(--status-red) 0%, #d54a3a 100%)"
                  : "linear-gradient(90deg, var(--status-green) 0%, #3a9559 100%)"
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
